import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Clock, Plus, Play, Trash2, ChevronDown, ChevronRight,
  CheckCircle, XCircle, Loader2, Calendar, Zap,
  Database, FileText, Bot, AlertCircle, RefreshCw,
  ArrowDown, MoreVertical, PencilLine
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STEP_TYPES = [
  { value: "ai_analysis",    label: "AI Analysis",     icon: Bot,       color: "text-[#A8A67A]",    bg: "bg-[#A8A67A]/10",  desc: "Ask the AI analyst to query and analyse portfolio data" },
  { value: "query_data",     label: "Query Data",      icon: Database,  color: "text-blue-600",     bg: "bg-blue-50",       desc: "Run a SQL query against the data warehouse" },
  { value: "generate_report",label: "Generate Report", icon: FileText,  color: "text-emerald-600",  bg: "bg-emerald-50",    desc: "Generate an institutional-grade PDF report" },
];

const SCHEDULE_TYPES = [
  { value: "manual",  label: "Manual only" },
  { value: "hourly",  label: "Hourly" },
  { value: "daily",   label: "Daily" },
  { value: "weekly",  label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

const defaultForm = () => ({
  name: "",
  description: "",
  schedule: { type: "manual", time: "08:00", day_of_week: 0, day_of_month: 1 },
  steps: [],
  enabled: true,
});

const stepTypeMeta = (type) => STEP_TYPES.find(s => s.value === type) || STEP_TYPES[0];

function scheduleLabel(sched) {
  if (!sched || sched.type === "manual") return "Manual";
  if (sched.type === "hourly") return `Every hour at :${String(sched.time?.split(":")[1] || "00").padStart(2,"0")}`;
  if (sched.type === "daily")  return `Daily at ${sched.time} UTC`;
  if (sched.type === "weekly") return `Weekly · ${DAYS[sched.day_of_week || 0]} ${sched.time} UTC`;
  if (sched.type === "monthly") return `Monthly · ${sched.day_of_month}${ordinal(sched.day_of_month)} at ${sched.time} UTC`;
  return sched.type;
}

function ordinal(n) {
  const s = ["th","st","nd","rd"], v = n % 100;
  return s[(v-20)%10] || s[v] || s[0];
}

function StatusBadge({ status }) {
  if (status === "success") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle className="w-2.5 h-2.5" />Success</span>;
  if (status === "failed")  return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full"><XCircle className="w-2.5 h-2.5" />Failed</span>;
  if (status === "running") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full"><Loader2 className="w-2.5 h-2.5 animate-spin" />Running</span>;
  return null;
}

function RunCard({ run }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border bg-white">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F8F9FA] transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
          <div>
            <p className="text-xs font-semibold text-foreground">
              {run.triggered_by === "manual" ? "Manual run" : "Scheduled run"}
            </p>
            <p className="text-[10px] text-muted-foreground">{new Date(run.started_at).toLocaleString()}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {run.completed_at && (
            <span className="text-[10px] text-muted-foreground">
              {((new Date(run.completed_at) - new Date(run.started_at)) / 1000).toFixed(1)}s
            </span>
          )}
          <StatusBadge status={run.status} />
        </div>
      </button>

      {open && run.results?.length > 0 && (
        <div className="border-t border-border divide-y divide-border">
          {run.results.map((r, i) => (
            <div key={i} className="px-4 py-3 bg-[#FAFAFA]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-foreground">{r.step_name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{r.duration_seconds}s</span>
                  <StatusBadge status={r.status} />
                </div>
              </div>
              <pre className="text-[10px] text-muted-foreground whitespace-pre-wrap font-mono bg-white border border-border p-2 max-h-40 overflow-y-auto leading-relaxed">
                {r.output || "(no output)"}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ScheduleTasks() {
  const [workflows, setWorkflows]   = useState([]);
  const [selected, setSelected]     = useState(null);   // workflow object
  const [mode, setMode]             = useState("list"); // list | build | view
  const [form, setForm]             = useState(defaultForm());
  const [editingId, setEditingId]   = useState(null);   // null = create new
  const [runs, setRuns]             = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [runningIds, setRunningIds] = useState(new Set());
  const [loadingWorkflows, setLoadingWorkflows] = useState(true);
  const [pollTimer, setPollTimer]   = useState(null);

  const loadWorkflows = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workflows`);
      const list = res.data.workflows || [];
      setWorkflows(list);
      // Keep selected in sync with latest server data
      setSelected(prev => {
        if (!prev) return prev;
        const fresh = list.find(w => w.workflow_id === prev.workflow_id);
        return fresh || prev;
      });
      return list;
    } finally {
      setLoadingWorkflows(false);
    }
  }, []);

  const loadRuns = useCallback(async (workflowId) => {
    setRunsLoading(true);
    try {
      const res = await axios.get(`${API}/workflows/${workflowId}/runs`);
      setRuns(res.data.runs || []);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => { loadWorkflows(); }, [loadWorkflows]);

  // Poll if any workflow is running
  useEffect(() => {
    if (runningIds.size > 0) {
      const t = setTimeout(async () => {
        await loadWorkflows();
        if (selected) await loadRuns(selected.workflow_id);
        setRunningIds(prev => {
          const next = new Set(prev);
          const latestRun = runs[0];
          if (latestRun && latestRun.status !== "running") {
            next.delete(selected?.workflow_id);
          }
          return next;
        });
      }, 3000);
      setPollTimer(t);
      return () => clearTimeout(t);
    }
  }, [runningIds, runs, selected, loadWorkflows, loadRuns]);

  const openBuilder = (wf = null) => {
    if (wf) {
      setForm({
        name: wf.name,
        description: wf.description || "",
        schedule: { ...wf.schedule },
        steps: wf.steps.map(s => ({ ...s })),
        enabled: wf.enabled,
      });
      setEditingId(wf.workflow_id);
    } else {
      setForm(defaultForm());
      setEditingId(null);
    }
    setMode("build");
    setSelected(wf);
  };

  const openDetail = async (wf) => {
    setSelected(wf);
    setMode("view");
    await loadRuns(wf.workflow_id);
  };

  const addStep = () => {
    setForm(f => ({
      ...f,
      steps: [...f.steps, {
        step_id: `step_${Date.now()}`,
        type: "ai_analysis",
        name: `Step ${f.steps.length + 1}`,
        prompt: "",
      }]
    }));
  };

  const removeStep = (idx) => {
    setForm(f => ({ ...f, steps: f.steps.filter((_, i) => i !== idx) }));
  };

  const updateStep = (idx, field, value) => {
    setForm(f => {
      const steps = [...f.steps];
      steps[idx] = { ...steps[idx], [field]: value };
      return { ...f, steps };
    });
  };

  const saveWorkflow = async () => {
    if (!form.name.trim()) { alert("Workflow name is required."); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/workflows/${editingId}`, form);
      } else {
        await axios.post(`${API}/workflows`, form);
      }
      await loadWorkflows();
      setMode("list");
      setSelected(null);
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const deleteWorkflow = async (wf) => {
    if (!window.confirm(`Delete "${wf.name}"?`)) return;
    await axios.delete(`${API}/workflows/${wf.workflow_id}`);
    await loadWorkflows();
    if (selected?.workflow_id === wf.workflow_id) { setSelected(null); setMode("list"); }
  };

  const runNow = async (wf) => {
    setRunningIds(prev => new Set(prev).add(wf.workflow_id));
    try {
      await axios.post(`${API}/workflows/${wf.workflow_id}/run`);
      if (selected?.workflow_id === wf.workflow_id) {
        setTimeout(() => loadRuns(wf.workflow_id), 1500);
      }
    } catch (err) {
      alert("Run failed: " + (err.response?.data?.detail || err.message));
      setRunningIds(prev => { const s = new Set(prev); s.delete(wf.workflow_id); return s; });
    }
  };

  const toggleEnabled = async (wf) => {
    await axios.put(`${API}/workflows/${wf.workflow_id}`, { enabled: !wf.enabled });
    await loadWorkflows();
    if (selected?.workflow_id === wf.workflow_id) {
      setSelected(wfs => ({ ...wfs, enabled: !wfs.enabled }));
    }
  };

  return (
    <div className="flex h-full bg-background" data-testid="schedule-tasks-page">
      {/* ── Left panel: workflow list ────────────────────────────── */}
      <aside className="w-72 bg-[#F8F9FA] border-r border-border flex flex-col flex-shrink-0">
        <div className="px-4 py-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mb-0.5">Ellerston Capital</p>
              <h2 className="text-sm font-bold">Ellerston Scheduler</h2>
            </div>
            <button
              onClick={() => loadWorkflows()}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="refresh-workflows-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            onClick={() => openBuilder(null)}
            className="w-full flex items-center justify-center gap-2 bg-[#A8A67A] text-white py-2 text-xs font-semibold hover:bg-[#8E8C64] transition-colors"
            data-testid="new-workflow-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            New Workflow
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {loadingWorkflows && (
            <div className="flex items-center justify-center h-20">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loadingWorkflows && workflows.length === 0 && (
            <div className="px-4 py-8 text-center">
              <Zap className="w-6 h-6 text-[#D4D2B0] mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-xs text-muted-foreground">No workflows yet.</p>
              <p className="text-[10px] text-muted-foreground">Click New Workflow to begin.</p>
            </div>
          )}
          {workflows.map(wf => (
            <div
              key={wf.workflow_id}
              onClick={() => openDetail(wf)}
              className={`group relative px-4 py-3 cursor-pointer border-l-2 transition-colors ${
                selected?.workflow_id === wf.workflow_id
                  ? "bg-white border-[#A8A67A] shadow-sm"
                  : "border-transparent hover:bg-white hover:border-[#A8A67A]/40"
              }`}
              data-testid={`workflow-item-${wf.name.replace(/\s+/g,"-").toLowerCase()}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-foreground truncate">{wf.name}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{scheduleLabel(wf.schedule)}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 ${
                      wf.enabled ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
                    }`}>
                      {wf.enabled ? "Active" : "Paused"}
                    </span>
                    {wf.run_count > 0 && (
                      <span className="text-[9px] text-muted-foreground">{wf.run_count} run{wf.run_count !== 1 ? "s" : ""}</span>
                    )}
                    {wf.last_run_status && <StatusBadge status={wf.last_run_status} />}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={e => { e.stopPropagation(); runNow(wf); }}
                    disabled={runningIds.has(wf.workflow_id)}
                    className="p-1 text-[#A8A67A] hover:bg-[#A8A67A]/10 rounded transition-colors disabled:opacity-40"
                    title="Run now"
                    data-testid={`run-btn-${wf.workflow_id}`}
                  >
                    {runningIds.has(wf.workflow_id)
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Play className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); openBuilder(wf); }}
                    className="p-1 text-muted-foreground hover:text-foreground hover:bg-white rounded transition-colors"
                    title="Edit"
                    data-testid={`edit-btn-${wf.workflow_id}`}
                  >
                    <PencilLine className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); deleteWorkflow(wf); }}
                    className="p-1 text-muted-foreground hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                    data-testid={`delete-btn-${wf.workflow_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* ── Right panel ──────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {/* Empty state */}
        {mode === "list" && (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <div className="w-16 h-16 bg-[#F5F4EE] flex items-center justify-center mb-4">
              <Zap className="w-8 h-8 text-[#D4D2B0]" strokeWidth={1} />
            </div>
            <h2 className="text-lg font-bold mb-2">Automate Your Workflows</h2>
            <p className="text-sm text-muted-foreground max-w-md mb-6">
              Build multi-step AI workflows — query data, analyse your portfolio, and generate reports on a schedule.
            </p>
            <button
              onClick={() => openBuilder(null)}
              className="flex items-center gap-2 bg-[#A8A67A] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#8E8C64] transition-colors"
              data-testid="create-first-workflow-btn"
            >
              <Plus className="w-4 h-4" />
              Create your first workflow
            </button>
          </div>
        )}

        {/* ── Builder ──────────────────────────────────────────── */}
        {mode === "build" && (
          <div className="p-6 max-w-2xl mx-auto" data-testid="workflow-builder">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">
                  {editingId ? "Edit Workflow" : "New Workflow"}
                </p>
                <h2 className="text-2xl font-bold">{form.name || "Untitled Workflow"}</h2>
              </div>
              <button onClick={() => { setMode("list"); setSelected(null); }} className="text-xs text-muted-foreground hover:text-foreground underline">Cancel</button>
            </div>

            {/* Name + description */}
            <div className="bg-white border border-border p-5 mb-2 space-y-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Workflow Name *</label>
                <input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Daily Portfolio Summary"
                  className="w-full px-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none bg-background"
                  data-testid="workflow-name-input"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Description</label>
                <input
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What does this workflow do?"
                  className="w-full px-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none bg-background"
                  data-testid="workflow-description-input"
                />
              </div>
            </div>

            {/* Connector */}
            <div className="flex justify-center my-0"><div className="w-px h-6 bg-border" /></div>

            {/* Trigger block */}
            <div className="bg-[#0A0A0A] border border-white/10 p-5 mb-0" data-testid="trigger-block">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-[#C4C29A]" strokeWidth={1.5} />
                <span className="text-xs font-semibold text-white uppercase tracking-wider">Trigger · Schedule</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1">Frequency</label>
                  <select
                    value={form.schedule.type}
                    onChange={e => setForm(f => ({ ...f, schedule: { ...f.schedule, type: e.target.value }}))}
                    className="w-full px-3 py-2 text-sm border border-white/20 bg-[#1A1A1A] text-white focus:outline-none focus:ring-1 focus:ring-[#C4C29A] rounded-none"
                    data-testid="schedule-type-select"
                  >
                    {SCHEDULE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                {form.schedule.type !== "manual" && (
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1">Time (UTC)</label>
                    <input
                      type="time"
                      value={form.schedule.time}
                      onChange={e => setForm(f => ({ ...f, schedule: { ...f.schedule, time: e.target.value }}))}
                      className="w-full px-3 py-2 text-sm border border-white/20 bg-[#1A1A1A] text-white focus:outline-none focus:ring-1 focus:ring-[#C4C29A] rounded-none"
                      data-testid="schedule-time-input"
                    />
                  </div>
                )}
                {form.schedule.type === "weekly" && (
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1">Day of Week</label>
                    <select
                      value={form.schedule.day_of_week}
                      onChange={e => setForm(f => ({ ...f, schedule: { ...f.schedule, day_of_week: Number(e.target.value) }}))}
                      className="w-full px-3 py-2 text-sm border border-white/20 bg-[#1A1A1A] text-white focus:outline-none focus:ring-1 focus:ring-[#C4C29A] rounded-none"
                      data-testid="schedule-dow-select"
                    >
                      {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  </div>
                )}
                {form.schedule.type === "monthly" && (
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1">Day of Month</label>
                    <input
                      type="number" min="1" max="28"
                      value={form.schedule.day_of_month}
                      onChange={e => setForm(f => ({ ...f, schedule: { ...f.schedule, day_of_month: Number(e.target.value) }}))}
                      className="w-full px-3 py-2 text-sm border border-white/20 bg-[#1A1A1A] text-white focus:outline-none focus:ring-1 focus:ring-[#C4C29A] rounded-none"
                      data-testid="schedule-dom-input"
                    />
                  </div>
                )}
              </div>
              {form.schedule.type !== "manual" && (
                <p className="text-[10px] text-white/40 mt-3">{scheduleLabel(form.schedule)}</p>
              )}
            </div>

            {/* Steps */}
            {form.steps.map((step, idx) => {
              const meta = stepTypeMeta(step.type);
              const Icon = meta.icon;
              return (
                <div key={step.step_id || idx}>
                  <div className="flex justify-center"><div className="w-px h-6 bg-border" /></div>
                  <div className="bg-white border border-border border-l-4 border-l-[#A8A67A] p-5" data-testid={`step-card-${idx}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-[#A8A67A]`}>{idx + 1}</div>
                        <input
                          value={step.name}
                          onChange={e => updateStep(idx, "name", e.target.value)}
                          className="text-sm font-semibold text-foreground border-0 border-b border-transparent hover:border-border focus:border-[#A8A67A] focus:outline-none bg-transparent w-48"
                          data-testid={`step-name-${idx}`}
                        />
                      </div>
                      <button onClick={() => removeStep(idx)} className="text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0" data-testid={`remove-step-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="mb-3">
                      <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Action Type</label>
                      <select
                        value={step.type}
                        onChange={e => updateStep(idx, "type", e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none"
                        data-testid={`step-type-${idx}`}
                      >
                        {STEP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <p className="text-[10px] text-muted-foreground mt-1">{meta.desc}</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                        {step.type === "query_data" ? "SQL Query" : step.type === "generate_report" ? "Report Notes (optional)" : "Prompt"}
                      </label>
                      <textarea
                        value={step.prompt}
                        onChange={e => updateStep(idx, "prompt", e.target.value)}
                        rows={step.type === "query_data" ? 3 : 4}
                        placeholder={
                          step.type === "query_data"
                            ? "SELECT * FROM internal_portfolio_holdings ORDER BY unrealized_pnl DESC LIMIT 10"
                            : step.type === "generate_report"
                            ? "Optional notes for the report..."
                            : "Analyse the current portfolio and identify the top 3 performing sectors with supporting data..."
                        }
                        className="w-full px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none placeholder:text-muted-foreground resize-none font-mono"
                        data-testid={`step-prompt-${idx}`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add step */}
            <div className="flex justify-center mt-0"><div className="w-px h-6 bg-border" /></div>
            <button
              onClick={addStep}
              className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-border text-sm text-muted-foreground hover:text-[#A8A67A] hover:border-[#A8A67A] hover:bg-[#F5F4EE] transition-colors"
              data-testid="add-step-btn"
            >
              <Plus className="w-4 h-4" />
              Add Step
            </button>

            {/* Save actions */}
            <div className="flex items-center gap-3 mt-6 pb-8">
              <button
                onClick={saveWorkflow}
                disabled={saving || !form.name.trim()}
                className="flex items-center gap-2 bg-[#A8A67A] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#8E8C64] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="save-workflow-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {saving ? "Saving..." : (editingId ? "Save Changes" : "Save & Schedule")}
              </button>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                  className="accent-[#A8A67A]"
                  data-testid="workflow-enabled-checkbox"
                />
                Enable on save
              </label>
            </div>
          </div>
        )}

        {/* ── Detail / Run History ──────────────────────────────── */}
        {mode === "view" && selected && (
          <div className="p-6 max-w-3xl mx-auto" data-testid="workflow-detail">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Workflow</p>
                <h2 className="text-2xl font-bold">{selected.name}</h2>
                {selected.description && <p className="text-sm text-muted-foreground mt-1">{selected.description}</p>}
                <div className="flex items-center gap-3 mt-2">
                  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 ${selected.enabled ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                    {selected.enabled ? "Active" : "Paused"}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />{scheduleLabel(selected.schedule)}
                  </span>
                  <span className="text-xs text-muted-foreground">{selected.run_count || 0} total run{selected.run_count !== 1 ? "s" : ""}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleEnabled(selected)}
                  className="px-3 py-1.5 text-xs border border-border text-muted-foreground hover:text-foreground hover:bg-[#F8F9FA] transition-colors"
                  data-testid="toggle-enabled-btn"
                >
                  {selected.enabled ? "Pause" : "Resume"}
                </button>
                <button
                  onClick={() => openBuilder(selected)}
                  className="px-3 py-1.5 text-xs border border-border text-muted-foreground hover:text-foreground hover:bg-[#F8F9FA] transition-colors"
                  data-testid="edit-workflow-btn"
                >
                  Edit
                </button>
                <button
                  onClick={() => runNow(selected)}
                  disabled={runningIds.has(selected.workflow_id) || !selected.steps?.length}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-[#A8A67A] text-white hover:bg-[#8E8C64] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="run-now-btn"
                >
                  {runningIds.has(selected.workflow_id)
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Running...</>
                    : <><Play className="w-3.5 h-3.5" />Run Now</>}
                </button>
              </div>
            </div>

            {/* Pipeline preview */}
            <div className="mb-8">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Pipeline</h3>
              {selected.steps?.length === 0 && (
                <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No steps configured. <button onClick={() => openBuilder(selected)} className="text-[#A8A67A] underline">Add steps</button>
                </div>
              )}
              {selected.steps?.map((step, idx) => {
                const meta = stepTypeMeta(step.type);
                const Icon = meta.icon;
                return (
                  <div key={step.step_id || idx}>
                    {idx > 0 && <div className="flex justify-center"><div className="w-px h-4 bg-border" /></div>}
                    <div className={`flex items-start gap-3 p-4 bg-white border border-border border-l-4 border-l-[#A8A67A]`}>
                      <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${meta.bg}`}>
                        <Icon className={`w-3.5 h-3.5 ${meta.color}`} strokeWidth={2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-semibold">{step.name}</span>
                          <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 ${meta.bg} ${meta.color}`}>{meta.label}</span>
                        </div>
                        {step.prompt && (
                          <p className="text-[10px] text-muted-foreground font-mono truncate">{step.prompt.slice(0, 120)}{step.prompt.length > 120 ? "…" : ""}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Run history */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Run History</h3>
                <button onClick={() => loadRuns(selected.workflow_id)} className="text-muted-foreground hover:text-foreground">
                  <RefreshCw className="w-3 h-3" />
                </button>
              </div>
              {runsLoading && (
                <div className="flex items-center justify-center h-16">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!runsLoading && runs.length === 0 && (
                <div className="border border-dashed border-border p-6 text-center">
                  <AlertCircle className="w-5 h-5 text-[#D4D2B0] mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-xs text-muted-foreground">No runs yet. Click Run Now to execute this workflow.</p>
                </div>
              )}
              <div className="space-y-2" data-testid="run-history">
                {runs.map(run => <RunCard key={run.run_id} run={run} />)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
